import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AddressEyebrow, Arrow, BackButton } from './QuoteElements';
import styled from 'styled-components';
import { solarData } from './SolarData';
import { Typography, Button, CircularProgress } from '@mui/material';
import BottomBar from './BottomBar';
import PricingPopup from './PricingPopup';
import { LeaseData } from './LeaseData';
import useIsMobile from "../../hooks/useIsMobile";
// import ScrollAnimation from './ScrollAnimation';
import useAutoDesigner, { designInputsOf, pricedExtrasOf } from '../../hooks/useAutoDesigner';
import ClosePopup from './ClosePopup';
import PackagePicker from './PackagePicker';
import CustomizeSection from './CustomizeSection';
import {
  financeProductFor,
  lenderForPromotion,
  packageHardware,
  packagePromotion,
  packageRules,
  resolvePromotion,
} from './Promotions';

export const HomeImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 12px;
  display: block;
  margin: 0 auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19);
`;

export const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: auto;
  top:0;
  z-index: 10;
  margin-bottom: 20px;
`;

export const ImageInternalWrapper = styled.div`
  height: 200px;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19);
  /* Anchors the "rendering your design" badge over the image. */
  position: relative;
  overflow: hidden;
`;

/**
 * The Design page is full width now.
 *
 * It used to render inside QuoteGrid's `1fr auto` split, with the looping video
 * on the left and every control crammed into a 380px right-hand rail. With the
 * video gone there is no second column, and the hardware collage, the package
 * cards and the eligibility strip all need room to sit side by side.
 *
 * Locate and Customer still use QuoteGrid and QuoteInnerWrapper, so those are
 * left alone — this pair is Design's own.
 */
const DesignPage = styled.div`
  width: 100%;
  height: 100%;
`;

const DesignWrapper = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 70px auto 10px;
  padding: 12px 24px 110px;

  /* Block, not flex.
   *
   * As a column flex container with a capped height, every child carried the
   * default flex-shrink of 1 — so once the accordions made the content taller
   * than the viewport, flex compressed the hero and the package cards down to
   * a few pixels rather than letting the container scroll. Block layout has no
   * shrinking, so the children keep their height and overflow-y does the work.
   */
  display: ${({ $ispopupopen }) => ($ispopupopen ? 'none' : 'block')};

  /* Minus the fixed 70px top bar this sits below, so the internal scroll
     region actually matches what is on screen. */
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  position: relative;
  filter: ${({ $ispopupopen }) => ($ispopupopen ? 'blur(5px)' : 'none')};
  opacity: ${({ $ispopupopen }) => ($ispopupopen ? '0.6' : '1')};
  pointer-events: ${({ $ispopupopen, disabled }) => ($ispopupopen || disabled ? 'none' : 'auto')};
  transition: filter 0.3s ease, opacity 0.3s ease;

  @media screen and (max-width: 900px) {
    margin-top: 60px;
    padding: 10px 14px 110px;
    max-height: none;
    overflow-y: unset;
  }
`;

/**
 * Back, then the address beneath it — both flush left, the way the pricing
 * popup's header reads.
 *
 * The address used to be the far side of a space-between row. Stacking is what
 * puts it against the left edge; it also lets it stretch the full width, which
 * is what makes its ellipsis work rather than a long address widening the row.
 * BackButton brings its own `align-self: flex-start`, so it still hugs its text.
 */
const DesignTopRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;

  /* Both children carry spacing tuned for the old stacked rail. */
  & > * {
    margin-top: 0;
  }

  /* BackButton paints the old grey card colour behind itself, which showed as a
     patch now that the page ground is white. */
  ${BackButton} {
    background-color: transparent;
  }
`;

const CustomContainer = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 20px;
  display: ${({$confirmed}) => ($confirmed ? 'none' : 'auto')};
`;

/**
 * The action bar that closes the page.
 *
 * Replaces a paragraph of caveats above a button. The paragraph explained that
 * the figures were estimates until a design ran — true, but it was four lines
 * of small grey text between the customer and the only thing to do next, and
 * the same caveat already sits in the disclosures on the pricing screen. The
 * bar restates what they are buying and gets out of the way.
 */
const ActionBar = styled.div`
  width: 100%;
  margin-top: 28px;
  padding: 16px 20px;
  border: 1px solid #e0e6ea;
  border-radius: 12px;
  background: #f6fafd;
  display: flex;
  align-items: center;
  gap: 34px;
  flex-wrap: wrap;

  @media screen and (max-width: 640px) {
    gap: 20px;
    padding: 14px 16px;
  }
`;

const BarFigure = styled.div``;

const BarKey = styled.span`
  display: block;
  font-size: 0.58rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 800;
  color: #8098a8;
`;

const BarValue = styled.span`
  font-size: 1rem;
  font-weight: 800;
  color: ${({ $promotion }) => ($promotion ? '#B06A00' : '#0F1B24')};
  font-variant-numeric: tabular-nums;
`;

const BarAction = styled.div`
  margin-left: auto;

  @media screen and (max-width: 640px) {
    margin-left: 0;
    width: 100%;
  }
`;

/* Keeps the purchase-only path (battery or charger, no solar) readable on a
   1180px page. */
const ActionWrapper = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const TopContainer = styled.div`
  width: 90%;
  max-width: 500px;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({bottombar}) => (bottombar ? '0' : '20px')};
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Value = styled.div`
  font-size: 1.4rem;
  font-weight: bold;
`;

export const Title = styled.div`
  font-size: 0.8rem;
  font-weight: normal;
  color: #555;
`;

const QuoteHeaderCustom = styled.h1`
  /* margin-top: 150px; */
  text-align: center;
  font-size: 1.6rem;
  color: #333 !important;
  display: ${({$confirmed}) => ($confirmed ? 'none' : 'auto')};
`;


export const StyledTypography = styled(Typography)`
  font-size: ${({$header}) => ($header ? '1.2rem !important' : '0.8rem !important')};
  color: ${({$header}) => ($header ? '#1976d2' : 'black')};
  font-weight: ${({$header}) => ($header ? 'bold !important' : 'normal')};
  margin: 5px 0;
  text-align: ${(props) => props.align || 'left'};
  text-decoration: ${({$strike}) => ($strike ? 'line-through' : 'none')};
`;

export const StyledSubtitle = styled(Typography)`
  font-size: 0.7rem !important;
  /* margin: 5px 0; */
  text-align: 'left';
  color: #777;
`;


export const TextFieldsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  /* margin: 20px 0; */
`;

const ButtonWrapper = styled.div`
  margin: 20px;
  display: flex;
  justify-content: center;
`

const StyledButton2 = styled(Button)`
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  /* display: flex;
  align-items: center; */
  margin-top: 20px;
  background-color: #1976d2 !important;
  color: white !important;

  &:hover {
    background-color: #1565c0 !important;
  }
`;





const SubmitMessageWrapper = styled.div`
    width: 90%;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media screen and (max-width: 480px) {
        width: 80%;
        margin: 0 auto;
    }

`

const SubmitMessageText = styled.div`
  text-align: center;
  color: ${({$success}) => ($success ? '#388e3c' : '#d32f2f')};
  font-size: 1.5rem;
  margin-top: 0.25rem;
`

/**
 * The page's call to action.
 *
 * Was an animated rainbow gradient bar with a glowing text shadow, docked to
 * the bottom of the old 380px rail. Against the rebuilt page — hairline rules,
 * one blue accent, orange held back for promotions — it read as a different
 * product. Solid brand blue, same job.
 */
const SolarButton = styled(Button)`
  min-height: 52px;
  width: 100%;
  background-color: #1ba8f1 !important;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0 auto;

  &:hover {
    background-color: #0b7fbd !important;
  }
`;

const SolarTypography = styled(Typography)`
  font-size: 0.8rem !important;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #04222f;
  font-weight: 800 !important;
  margin: 0;
`;

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString('en-US')}`;

/* ---------------------------------------------------------------- pricing math

   These sit at module scope because two callers need them: the pricing effect
   below, and the price previews on the package switcher. Battery pricing in
   particular is the kind of thing that quietly grows a second implementation
   and then diverges from the first. */

const solarCostFor = ({ moduleOption, inverterOption, systemSize, state }) => {
  const ppw = state === 'FL' ? moduleOption.ppw : moduleOption.ppwNE;
  const solarPPW = ppw + (inverterOption.ppw || 0);
  return { solarCost: solarPPW * systemSize, solarPPW };
};

const batteryCostFor = ({ batteryOption, inverterOption, systemSize, batteryQuantity }) => {
  // A Powerwall 3 on the Tesla inverter is priced as one integrated unit.
  if (
    inverterOption.name === 'Tesla Inverter' &&
    systemSize > 0 &&
    batteryOption.name === 'Tesla Powerwall 3' &&
    batteryQuantity > 0
  ) {
    return batteryOption.inverterPrice * batteryQuantity;
  }

  const initialBatteryCost = batteryQuantity > 0 ? batteryOption.firstUnit : 0;
  const additionalBatteryCost =
    batteryQuantity > 1 ? batteryOption.additionalUnits * (batteryQuantity - 1) : 0;

  return initialBatteryCost + additionalBatteryCost;
};

/**
 * Monthly payment for a finance product.
 *
 * Two shapes. Credit Human — the lender behind the Participate promotions — is
 * quoted as a payment factor: the payment is simply the financed amount times
 * that number, with no amortisation schedule behind it, so running it through
 * the APR formula would invent a figure the lender never quoted.
 *
 * Climate First is a conventional loan that holds the first six months, so
 * interest accrues on the balance before the first bill and the remaining term
 * is one month short.
 */
const loanMonthlyPayment = ({ principal, product }) => {
  if (product.factor) return principal * product.factor;

  const monthlyRate = (product.apr / 100) / 12;
  const payments = (product.term * 12) - 1;
  if (!(payments > 0)) return 0;
  if (monthlyRate === 0) return principal / payments;

  const balanceAtFirstPayment = principal * ((1 + monthlyRate) ** 6);
  return (balanceAtFirstPayment * monthlyRate) / (1 - (1 + monthlyRate) ** -payments);
};

/** What the customer finances after any lender fee and down payment. */
const financedPrincipal = ({ total, product, downPayment }) =>
  (total / ((100 - product.fee) / 100)) - (Number(downPayment) || 0);

const Design = ({
  formState,
  responseState,
  nextButton,
  backButton,
  satelliteImageUrl,
  hardware,
  updateHardware,
  updateLender,
  updateProduct,
  updateSolutions
}) => {
  const [view, setView] = useState('Modules');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [designLocked, setDesignLocked] = useState(false);
  const scrollContainerRef = useRef(null);
  const isMobile = useIsMobile();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const { designStatus, setDesignStatus, startAutomation, createDesign, stopPolling, resetDesignState, generatedInputs, generatedExtras } = useAutoDesigner({
    hardware,
    updateHardware,
    responseState,
    formState,
    setDesignLocked
  })

  // What the primary button should do when the customer comes back to this page
  // after a design already exists.
  //
  // Batteries and EV chargers are priced locally and never sent to Aurora, so
  // changing them cannot change the panel layout. Regenerating for them would
  // cost the customer a minute of waiting and spend Aurora credits to arrive at
  // an identical design, so the button only regenerates when an input Aurora
  // actually designs against has changed.
  const hasDesign = Boolean(generatedInputs);
  const designInputsChanged = hasDesign && designInputsOf(hardware) !== generatedInputs;
  const extrasChanged = hasDesign && pricedExtrasOf(hardware) !== generatedExtras;

  const primaryAction = !hasDesign
    ? { label: 'Start Generating Design', regenerate: true }
    : designInputsChanged
      ? { label: 'Regenerate Design', regenerate: true }
      : extrasChanged
        ? { label: 'Update System', regenerate: false }
        : { label: 'Back to My System', regenerate: false };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleBarClick = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const handleAddSolar = () => {
    updateHardware('systemSize', responseState.estimateSize);
    updateSolutions(1);
  };


  /**
   * The quantity sliders now start at zero, so they carry the add/remove
   * decision the separate buttons used to. `solutions` still drives which
   * sections the customer asked for, and one of the pricing effects puts an EV
   * charger back at one whenever solutions holds 3 — so dropping to zero has to
   * take the solution out with it or the slider springs back.
   */
  const handleBatteryQuantityChange = (newValue) => {
    updateHardware('batteryQuantity', newValue);
    // Stop the pricing effect from replacing this with its own recommendation.
    updateHardware('batteryState', false);

    if (newValue === 0 && hardware.inverter.name === 'Tesla Powerwall 3') {
      updateHardware('inverter', solarData.inverters.options[0]);
    }
    if ((newValue > 0) !== hardware.solutions.includes(2)) updateSolutions(2);
  };

  const handleServiceQuantityChange = (newValue) => {
    updateHardware('serviceQuantity', newValue);
    updateHardware('serviceCost', newValue * hardware.services.price);

    if ((newValue > 0) !== hardware.solutions.includes(3)) updateSolutions(3);
  };

  const handleInverterChange = (newInverter) => {
    updateHardware('inverter', newInverter);

    if (newInverter.name === 'Tesla Powerwall 3' && hardware.battery.name !== 'Tesla Powerwall 3') {
      updateHardware('battery', solarData.batteries.options[0]);
      if(hardware.batteryQuantity === 0){
        updateHardware('batteryQuantity', hardware.recommendedBattQuantity);
        updateSolutions(2);
      }
    }
    if (newInverter.name === 'Tesla Powerwall 3' && hardware.battery.name === 'Tesla Powerwall 3') {
      if(hardware.batteryQuantity === 0){
        updateHardware('batteryQuantity', hardware.recommendedBattQuantity);
        updateSolutions(2);
      }
    }
  };

  const handleBatteryChange = (newBattery) => {
    // console.log("changing battery");
    updateHardware('battery', newBattery);
    updateHardware('batteryState', true);
    // updateHardware('batteryQuantity', hardware.recommendedBattQuantity);
    if (hardware.inverter.name === 'Tesla Powerwall 3' && newBattery.name !== 'Tesla Powerwall 3') {
      updateHardware('inverter', solarData.inverters.options[0]);
    }
    // updateHardware('batteryQuantity', hardware.recommendedBattQuantity);
    // if(hardware.battery.name === '')
  };

  /**
   * Loads a package's hardware. Deliberately leaves the system size alone: that
   * comes from the customer's own bill, and quietly enlarging their array to
   * clear a promotion threshold would be selling them power they didn't ask
   * for. The eligibility strip tells them they're short instead.
   */
  const handleSelectPackage = (pkg) => {
    const kit = packageHardware(pkg);

    updateHardware('selectedPackage', pkg.id);
    if (kit.module) updateHardware('module', kit.module);
    if (kit.inverter) updateHardware('inverter', kit.inverter);
    if (kit.battery) updateHardware('battery', kit.battery);
    updateHardware('batteryQuantity', kit.batteryQuantity);

    // The pricing effect resets the battery count to its own recommendation
    // while batteryState is true, which would immediately undo the package.
    updateHardware('batteryState', false);

    // A package is the primary call to action now, so picking one adds solar
    // for a customer who hasn't yet.
    if (hardware.systemSize === 0) {
      updateHardware('systemSize', responseState.estimateSize);
      if (!hardware.solutions.includes(1)) updateSolutions(1);
    }

    // Keep the battery accordion in step with what the package includes.
    const includesBattery = kit.batteryQuantity > 0;
    if (includesBattery !== hardware.solutions.includes(2)) updateSolutions(2);
  };

  /**
   * The array size a package would land on, rounded up to whole panels of its
   * own module. Anchored on the target the customer asked for rather than on
   * the current system size, which is already rounded to whichever panel
   * happens to be selected.
   */
  const previewSystemSize = useCallback((moduleOption) => {
    const watts = moduleOption?.watts;
    const target = Number(hardware.targetWatts) || Number(hardware.systemSize) || 0;
    if (!watts || !target) return hardware.systemSize;
    return Math.ceil(target / watts) * watts;
  }, [hardware.targetWatts, hardware.systemSize]);

  /**
   * What a package would cost this customer, for the switcher.
   *
   * Priced against their actual system size and EV chargers, so the figures on
   * the cards are the figures they would really pay — including the case where
   * their array is too small to clear the FoxESS threshold and the discount
   * doesn't apply.
   */
  const previewPackage = useCallback((pkg) => {
    const kit = packageHardware(pkg);
    if (!kit.module || !kit.inverter || !kit.battery) return null;
    if (!(hardware.systemSize > 0)) return null;

    // Each card is priced in its own context, not the selected build's.
    //
    // Two things used to leak across. The array was measured in the currently
    // selected panel, so switching between a 455W and a 410W module re-rounded
    // it and repriced every card; and the monthly came from whichever lender the
    // current promotion had selected, so all four cards flipped between an 8%
    // loan and a payment factor. A card now answers one question only: what this
    // package would cost, whatever is selected right now.
    const systemSize = previewSystemSize(kit.module);

    const { solarCost } = solarCostFor({
      moduleOption: kit.module,
      inverterOption: kit.inverter,
      systemSize,
      state: formState.state,
    });
    const batteryCost = batteryCostFor({
      batteryOption: kit.battery,
      inverterOption: kit.inverter,
      systemSize,
      batteryQuantity: kit.batteryQuantity,
    });
    const serviceCost = hardware.serviceQuantity * hardware.services.price;
    const subtotal = solarCost + batteryCost + serviceCost;

    const build = { ...hardware, ...kit, systemSize };
    const qualifies = packageRules(pkg.id, build).every((rule) => rule.met);
    const promotion = qualifies ? packagePromotion(pkg.id, subtotal) : null;
    const total = subtotal - (promotion ? promotion.amount : 0);

    const product = financeProductFor(lenderForPromotion(promotion));
    const monthly =
      product && product.type === 'Loan'
        ? loanMonthlyPayment({
            principal: financedPrincipal({ total, product, downPayment: hardware.downPayment }),
            product,
          })
        : 0;

    return { total, monthly, promotion, systemSize };
  }, [hardware, formState.state, previewSystemSize]);

  const handleStartGeneration = () => {
    // Only spend a design run when Aurora's inputs actually changed. Adding a
    // battery or an EV charger just re-prices the existing design, so the
    // customer goes straight back to their results.
    if (primaryAction.regenerate) {
      setDesignLocked(true);
      startAutomation();
    }
    handleBarClick();
  };




  const backButtonDesign = () => {
    backButton();
    stopPolling();
  }

  async function handleSignUp() {
    setSubmitting(true);
    setError(false);

    const data = JSON.stringify({
      "address": formState.address,
      "hardware":hardware,
    });

    const config = {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data
    };

    const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate`, config);

    if (response.ok) {
      setConfirmed(true);
      setSuccess('Thank you! You will receive an email shortly!');
    } else {
      setError('Sign Up failed');
    }

    setSubmitting(false);
  }

  useEffect(() => {
    // Helper function to calculate solar cost
    const calculateSolarCost = (moduleOption, inverterOption) =>
      solarCostFor({
        moduleOption,
        inverterOption,
        systemSize: hardware.systemSize,
        state: formState.state,
      });

    // Helper function to calculate battery costs
    const calculateBatteryCosts = (batteryOption, inverterOption) => ({
      batteryCost: batteryCostFor({
        batteryOption,
        inverterOption,
        systemSize: hardware.systemSize,
        batteryQuantity: hardware.batteryQuantity,
      }),
    });

    // Helper function to calculate service costs
    const calculateServiceCost = (serviceOption) => {
      return hardware.serviceQuantity * serviceOption.price;
    };

    // Helper function to calculate financials
    //
    // There is one payment figure now, not two. The old pair existed because a
    // tax credit arrived months after signing: the customer paid one amount
    // until the refund landed, dropped it on the loan, and re-amortised. A
    // promotion is taken off the price at checkout, so there is nothing to drop
    // on the loan later and nothing to re-amortise.
    const calculateFinancials = (totalAfterPromotion, downPayment) => {
      let financedMonthlyPayment = 0,
        financedTotal = 0;

      if (hardware.selectedProduct.type === 'Loan') {
        const principal = financedPrincipal({
          total: totalAfterPromotion,
          product: hardware.selectedProduct,
          downPayment,
        });

        financedMonthlyPayment = loanMonthlyPayment({
          principal,
          product: hardware.selectedProduct,
        });
        financedTotal = principal.toFixed(2);
      } else if (hardware.selectedProduct.type === 'Lease') {
        const matchingRateData = LeaseData.find(data => data.State === formState.state);

        financedTotal = (totalAfterPromotion / ((100 - hardware.selectedProduct.fee) / 100)).toFixed(2);

        const totalCostWithFee = (responseState.solarProductionMonthly*12)*matchingRateData.Rate*hardware.selectedProduct.term;
        const rate = ((totalCostWithFee)+(totalCostWithFee * (hardware.selectedProduct.fee/100)))/((responseState.solarProductionMonthly*12)*hardware.selectedProduct.term);
        financedMonthlyPayment = rate * hardware.systemSize;

      }
      return { financedTotal, financedMonthlyPayment };
    };

    // Helper function to calculate backup hours and recommended battery quantity
    const calculateBackupInfo = (batteryOption, batteryCapacity) => {
      const backupHours = Math.round((batteryCapacity * 0.8) / ((((responseState.solarProductionMonthly * 12) / 365) / 24) * 0.75));
      const minBatteryCapacity = 12 * (((responseState.solarProductionMonthly * 12) / 365 / 24) * 0.75) / 0.8;
      const recommendedBattQuantity = Math.min(Math.ceil(minBatteryCapacity / batteryOption.capacity), 7);

      return { backupHours, recommendedBattQuantity };
    };

    // Main calculation logic
    const calculateCosts = () => {
      const moduleOption = solarData.modules.options.find(option => option.name === hardware.module.name);
      const inverterOption = solarData.inverters.options.find(option => option.name === hardware.inverter.name);
      const batteryOption = solarData.batteries.options.find(option => option.name === hardware.battery.name);
      const serviceOption = solarData.services.options.find(option => option.name === hardware.services.name);

      // console.log("batt option ", batteryOption);

      const {solarCost, solarPPW} = calculateSolarCost(moduleOption, inverterOption);
      const {batteryCost} = calculateBatteryCosts(batteryOption, inverterOption);
      // Round at the source, not at the point of display: the per-unit capacities
      // are fractional (FoxESS is 3.97 kWh) so the multiplication carries binary
      // float noise — 7 units rendered as 27.790000000000003 kWh. Rounding here
      // means every consumer (display, backup-hours math, the hardware blob sent
      // to the backend) sees the same clean number.
      const batteryCapacity = Math.round(hardware.batteryQuantity * batteryOption.capacity * 100) / 100;
      const serviceCost = calculateServiceCost(serviceOption);

      const { backupHours, recommendedBattQuantity } = calculateBackupInfo(batteryOption, batteryCapacity);
      const totalCost = solarCost + batteryCost + serviceCost;

      // Promotions replace the 30% federal tax credit this flow used to quote
      // against. Resolved from the build rather than from the package card the
      // customer clicked, so a system assembled by hand earns the same discount
      // as one loaded from a card, and drifting off-spec falls back to standard
      // pricing without anything to undo.
      const promotion = resolvePromotion(hardware, totalCost);
      const promotionAmount = promotion ? promotion.amount : 0;
      const totalAfterPromotion = totalCost - promotionAmount;

      if(hardware.solutions.includes(2) && hardware.batteryState){
        // console.log("updating batt quant to recomended:", recommendedBattQuantity);
        updateHardware('batteryQuantity', recommendedBattQuantity);
        updateHardware('batteryState', false);
      }

      // console.log("downPayment updated in Design:", hardware.downPayment);
      const { financedTotal, financedMonthlyPayment } = calculateFinancials(totalAfterPromotion, hardware.downPayment);

      return {
        solar: solarCost,
        solarPPW: solarPPW,
        battery: batteryCost,
        backupHours,
        recommendedBattQuantity,
        batteryCapacity,
        service: serviceCost,
        total: totalCost,
        promotion,
        promotionAmount,
        totalAfterPromotion,
        financedTotal,
        financedMonthlyPayment
      };
    };

    // Call calculateCosts and destructure the results
    const {
      solar,
      solarPPW,
      battery,
      backupHours,
      recommendedBattQuantity,
      batteryCapacity,
      service,
      total,
      promotion,
      promotionAmount,
      totalAfterPromotion,
      financedTotal,
      financedMonthlyPayment
    } = calculateCosts();

    // Update hardware state
    updateHardware('solarCost', solar);
    updateHardware('solarPPW', solarPPW);
    updateHardware('batteryCost', battery);
    updateHardware('backupHours', backupHours);
    updateHardware('recommendedBattQuantity', recommendedBattQuantity);
    updateHardware('batteryCapacity', batteryCapacity);
    updateHardware('serviceCost', service);
    updateHardware('totalCost', total);
    updateHardware('promotion', promotion);
    updateHardware('promotionAmount', promotionAmount);
    updateHardware('totalAfterPromotion', totalAfterPromotion);
    updateHardware('financedTotal', financedTotal);
    updateHardware('financedMonthlyPayment', financedMonthlyPayment);

    // The Participate prices are only available through Credit Human, so the
    // lender follows the promotion rather than being a dropdown a customer can
    // lose 30% in. Cash keeps the discount either way — that is a payment type,
    // not a lender.
    const requiredLender = lenderForPromotion(promotion);
    if (hardware.selectedLender !== requiredLender) {
      updateLender(requiredLender);
    }

    // Legacy field names the backend still reads off this blob — the rep deal
    // email and the design link. They now carry the promotion figures, and the
    // two monthly payments are the same number because there is no longer a
    // second, post-credit payment. Remove once the backend is renamed.
    updateHardware('taxCredit', promotionAmount);
    updateHardware('totalAfterTaxCredit', totalAfterPromotion);
    updateHardware('financedMonthlyPaymentPreTaxCredit', financedMonthlyPayment);
    updateHardware('financedMonthlyPaymentAfterTaxCredit', financedMonthlyPayment);

    // Check and update solution states
    if (hardware.solutions.includes(1) && hardware.systemSize === 0) {
      updateHardware('systemSize', responseState.estimateSize);
    }
    // if (hardware.solutions.includes(2) && hardware.batteryQuantity === 0) {
    //   updateHardware('batteryQuantity', hardware.recommendedBattQuantity);
    // }
    
    // if (hardware.solutions.includes(2)) {
    //   updateHardware('batteryQuantity', hardware.recommendedBattQuantity);
    // }
    if (hardware.solutions.includes(3) && hardware.serviceQuantity === 0) {
      updateHardware('serviceQuantity', 1);
    }

    if (hardware.solutions.includes(1) && hardware.systemSize === 0 && hardware.solutions.includes(2) && hardware.batteryQuantity === 0){
      updateHardware('inverter', solarData.inverters.options[0])
    }

  }, [
    hardware.systemSize,
    hardware.module,
    hardware.inverter,
    hardware.battery,
    hardware.batteryQuantity,
    hardware.recommendedBattQuantity,
    hardware.batteryState,
    hardware.additionalPowerwallCost,
    hardware.solarCost,
    hardware.batteryCost,
    hardware.serviceCost,
    hardware.services,
    hardware.selectedProduct,
    hardware.selectedLender,
    hardware.totalAfterPromotion,
    updateHardware,
    updateLender,
    formState.state,
    responseState.solarProductionMonthly,
    hardware.serviceQuantity,
    hardware.solutions,
    responseState.estimateSize,
    hardware.downPayment,
    designStatus
  ]);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, []);

  return (
    <DesignPage>
      {/* The looping background video used to sit here, in the left half of a
          two-column grid, with every control squeezed into a 380px rail on the
          right. Both are gone: the page is one full-width column now. */}
      {showConfirmation &&
        <ClosePopup
          setIsPopupOpen={setIsPopupOpen}
          showConfirmation={showConfirmation}
          setShowConfirmation={setShowConfirmation}
          resetDesignState={resetDesignState}
        />
      }
      {isPopupOpen && (
        <PricingPopup
          systemSize={hardware.systemSize}
          hardware={hardware}
          updateHardware={updateHardware}
          updateLender={updateLender}
          updateProduct={updateProduct}
          onClick={setShowConfirmation}
          responseState={responseState}
          formState={formState}
          satelliteImageUrl={satelliteImageUrl}
          setDesignLocked={setDesignLocked}
          setDesignStatus={setDesignStatus}
          designStatus={designStatus}
          // setShowConfirmation={setShowConfirmation}
        />
      )}
      {hardware.systemSize > 0 && designLocked &&
        <BottomBar
          systemSize={hardware.systemSize}
          hardware={hardware}
          onClick={handleBarClick}
          formState={formState}
          responseState={responseState}
          updateHardware={updateHardware}
          designLocked={designLocked}
          setDesignLocked={setDesignLocked}
          designStatus={designStatus}
          startAutomation={startAutomation}
        />
      }

      {(!isMobile || !isPopupOpen) &&
        <DesignWrapper ref={scrollContainerRef} $ispopupopen={isPopupOpen}>
          <DesignTopRow>
            <BackButton onClick={backButtonDesign}>
              <Arrow>&lt;</Arrow>
              Back
            </BackButton>
            <AddressEyebrow>{formState.address}</AddressEyebrow>
          </DesignTopRow>

          {/* The roof render is not shown here any more. Before a design runs it
              was a plain satellite photo with no panels on it, which read as a
              finished design; the real render belongs to the pricing popup one
              step later. */}
          {/* Not gated on `solutions` containing solar. That flag is only ever
              set by the product picker two screens back, and a customer who
              lands here without it saw no packages at all — the headline of the
              page silently missing. Picking a package adds solar itself. */}
          {!confirmed && (
            <PackagePicker
              hardware={hardware}
              selectedPackage={hardware.selectedPackage}
              onSelectPackage={handleSelectPackage}
              annualProduction={hardware.annualProduction || responseState.solarProductionMonthly * 12}
              subtotal={hardware.totalCost}
              monthlyPayment={hardware.financedMonthlyPayment}
              estimateMonthly={previewPackage}
            />
          )}

          <CustomContainer $confirmed={confirmed}>
            <CustomizeSection
              hardware={hardware}
              responseState={responseState}
              updateHardware={updateHardware}
              onModuleChange={(option) => updateHardware('module', option)}
              onInverterChange={handleInverterChange}
              onBatteryChange={handleBatteryChange}
              onBatteryQuantityChange={handleBatteryQuantityChange}
              onServiceQuantityChange={handleServiceQuantityChange}
              onAddSolar={handleAddSolar}
            />
          </CustomContainer>
          {hardware.systemSize > 0 ? (
            <ActionBar>
              <BarFigure>
                <BarKey>System</BarKey>
                <BarValue>{(hardware.systemSize / 1000).toFixed(2)} kW</BarValue>
              </BarFigure>
              <BarFigure>
                <BarKey>Your price</BarKey>
                <BarValue>{money(hardware.totalAfterPromotion)}</BarValue>
              </BarFigure>
              <BarFigure>
                <BarKey>Promotion</BarKey>
                <BarValue $promotion={hardware.promotionAmount > 0}>
                  {hardware.promotionAmount > 0 ? `−${money(hardware.promotionAmount)}` : 'None'}
                </BarValue>
              </BarFigure>
              <BarFigure>
                <BarKey>Monthly</BarKey>
                <BarValue>{money(hardware.financedMonthlyPayment)}/mo</BarValue>
              </BarFigure>
              <BarAction>
                <SolarButton onClick={handleStartGeneration}>
                  <SolarTypography>{primaryAction.label}</SolarTypography>
                </SolarButton>
              </BarAction>
            </ActionBar>
          ) : (
            <ActionWrapper>
              {(hardware.serviceQuantity > 0 || hardware.batteryQuantity > 0) &&
                <>
                  <QuoteHeaderCustom>Pricing</QuoteHeaderCustom>
                  {hardware.serviceCost !== 0 && (
                    <>
                      <TextFieldsWrapper>
                        <StyledTypography>
                          {hardware.serviceQuantity} - {hardware.services.name}{hardware.serviceQuantity === 1 ? '' : "'s"}
                        </StyledTypography>
                        <StyledTypography>
                          ${Math.round(hardware.serviceCost).toLocaleString()}
                        </StyledTypography>
                      </TextFieldsWrapper>
                    </>
                  )}
                  {hardware.batteryQuantity > 0 && (
                    <>
                      <TextFieldsWrapper>
                        <StyledTypography>
                          {hardware.batteryQuantity} - {hardware.battery.name}{hardware.batteryQuantity === 1 ? '' : "'s"}
                        </StyledTypography>
                        <StyledTypography>
                          ${Math.round(hardware.batteryCost).toLocaleString()}
                        </StyledTypography>
                      </TextFieldsWrapper>
                    </>
                  )}

                  {(hardware.batteryQuantity !== 0 || hardware.serviceCost !== 0) &&
                    <>
                      <TextFieldsWrapper>
                        <StyledTypography $header>
                          Total Cost
                        </StyledTypography>
                        <StyledTypography $header>
                          ${Math.round(hardware.batteryCost + hardware.serviceCost).toLocaleString()}
                        </StyledTypography>
                      </TextFieldsWrapper>
                    </>
                  }
                  {!success &&
                    <ButtonWrapper>
                      {submitting ? <CircularProgress size={24}/> : <StyledButton2 onClick={handleSignUp}>Purchase Now</StyledButton2>}
                    </ButtonWrapper>
                  }
                  {(success || error) &&
                    <SubmitMessageWrapper>
                      {success && <SubmitMessageText $success>{success}</SubmitMessageText>}
                      {error && <SubmitMessageText>{error}</SubmitMessageText>}
                    </SubmitMessageWrapper>
                  }
                </>
              }
            </ActionWrapper>
          )}
        </DesignWrapper>
      }
    </DesignPage>
  );
};

export default Design;
